import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import AdminLayout from '@/components/Layout';
import { Layers, Plus, Trash2, ArrowUp, ArrowDown, Pencil, X, Save, Loader2 } from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'live', label: 'Canlı TV' },
  { value: 'vod', label: 'VOD' },
  { value: 'series', label: 'Dizi' },
];

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCat, setNewCat] = useState({ name: '', type: 'live', order: 0 });
  const [loading, setLoading] = useState(true);

  // Edit state
  const [editCat, setEditCat] = useState(null);     // category being edited (or null)
  const [editBusy, setEditBusy] = useState(false);
  const [editErr, setEditErr] = useState('');

  const load = async () => {
    try {
      const res = await api.get('/categories');
      // sort by order asc as a safety net
      const sorted = [...res.data].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setCategories(sorted);
    } catch (err) {
      console.error('Kategori yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      // auto-assign next order if left at 0
      const nextOrder = newCat.order || (categories.reduce((max, c) => Math.max(max, c.order ?? 0), 0) + 1);
      await api.post('/categories', { ...newCat, order: nextOrder });
      setShowAddModal(false);
      setNewCat({ name: '', type: 'live', order: 0 });
      load();
    } catch (err) {
      alert('Kategori eklenemedi.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kategoriyi silmek istediğinize emin misiniz? Bağlı kanallar kategorisiz kalacak.')) return;
    try {
      await api.delete(`/categories/${id}`);
      setCategories(categories.filter(c => c._id !== id));
    } catch (err) {
      alert('Silinemedi.');
    }
  };

  // Open edit modal pre-filled with the row's data
  const openEdit = (cat) => {
    setEditErr('');
    setEditCat({ _id: cat._id, name: cat.name || '', type: cat.type || 'live',
                 order: cat.order ?? 0, is_active: cat.is_active !== false });
  };
  const handleEditSave = async (e) => {
    e.preventDefault();
    if (!editCat) return;
    setEditBusy(true); setEditErr('');
    const { _id, ...patch } = editCat;
    try {
      const res = await api.patch(`/categories/${_id}`, patch);
      const updated = res.data;
      // Optimistic update + re-sort by order
      setCategories(prev => {
        const next = prev.map(c => (c._id === _id ? { ...c, ...updated } : c));
        return next.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      });
      setEditCat(null);
      toast.success('Kategori güncellendi');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Kaydedilemedi.';
      setEditErr(msg);
      toast.error(msg);
    } finally { setEditBusy(false); }
  };

  // Swap order with neighbour (up=-1, down=+1)
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= categories.length) return;
    const a = categories[index];
    const b = categories[target];
    const aOrder = a.order ?? index;
    const bOrder = b.order ?? target;
    // optimistic UI swap
    const next = [...categories];
    next[index] = { ...b, order: aOrder };
    next[target] = { ...a, order: bOrder };
    next.sort((x, y) => x.order - y.order);
    setCategories(next);
    try {
      await Promise.all([
        api.patch(`/categories/${a._id}`, { order: bOrder }),
        api.patch(`/categories/${b._id}`, { order: aOrder }),
      ]);
    } catch (err) {
      alert('Sıralama güncellenemedi, yeniden yükleniyor.');
      load();
    }
  };

  const typeLabel = (t) => TYPE_OPTIONS.find(o => o.value === t)?.label || t;

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-3" data-testid="categories-page-title">
            <Layers className="text-amber-500" /> Kategori Yönetimi
          </h1>
          <button
            onClick={() => setShowAddModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-black px-4 py-2 rounded-lg font-bold flex items-center gap-2 transition-transform active:scale-95"
            data-testid="categories-add-btn"
          >
            <Plus size={20} /> Yeni Kategori
          </button>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden shadow-2xl">
          <table className="w-full text-left text-white" data-testid="categories-table">
            <thead className="bg-slate-950 text-gray-400 text-xs uppercase font-bold tracking-wider">
              <tr>
                <th className="p-4 w-24 text-center">Sıra</th>
                <th className="p-4 font-semibold text-white">Ad</th>
                <th className="p-4 font-semibold text-white">Tip</th>
                <th className="p-4 font-semibold text-white">Durum</th>
                <th className="p-4 text-right">İşlem</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                <tr><td colSpan="5" className="p-12 text-center text-gray-500 italic">Yükleniyor…</td></tr>
              ) : categories.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-12 text-center text-gray-500 italic" data-testid="categories-empty">
                    Henüz kategori yok. "Yeni Kategori" butonuna tıklayın.
                  </td>
                </tr>
              ) : (
                categories.map((cat, idx) => (
                  <tr key={cat._id} className="hover:bg-slate-800/30 transition-colors group" data-testid={`category-row-${cat._id}`}>
                    <td className="p-4 text-center">
                      <div className="inline-flex flex-col items-center gap-0.5">
                        <button
                          onClick={() => move(idx, -1)}
                          disabled={idx === 0}
                          className="text-gray-500 hover:text-amber-500 disabled:opacity-20 disabled:hover:text-gray-500 p-0.5 transition-colors"
                          title="Yukarı taşı"
                          data-testid={`category-up-${cat._id}`}
                        >
                          <ArrowUp size={14} />
                        </button>
                        <span className="text-[10px] font-mono text-gray-500">{cat.order ?? idx + 1}</span>
                        <button
                          onClick={() => move(idx, +1)}
                          disabled={idx === categories.length - 1}
                          className="text-gray-500 hover:text-amber-500 disabled:opacity-20 disabled:hover:text-gray-500 p-0.5 transition-colors"
                          title="Aşağı taşı"
                          data-testid={`category-down-${cat._id}`}
                        >
                          <ArrowDown size={14} />
                        </button>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-bold text-white">{cat.name}</div>
                    </td>
                    <td className="p-4">
                      <span className="text-xs px-2.5 py-1 bg-slate-800 text-slate-300 rounded-full border border-slate-700 font-medium">
                        {typeLabel(cat.type)}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className={`flex items-center gap-1.5 text-[10px] font-bold uppercase ${cat.is_active ? 'text-green-500' : 'text-gray-500'}`}>
                        <div className={`w-1.5 h-1.5 rounded-full ${cat.is_active ? 'bg-green-500' : 'bg-gray-500'}`} />
                        {cat.is_active ? 'Aktif' : 'Pasif'}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => openEdit(cat)}
                          className="text-gray-500 hover:text-amber-400 hover:bg-amber-500/10 p-2 rounded-lg transition-all duration-200"
                          data-testid={`category-edit-btn-${cat._id}`}
                          title="Kategoriyi düzenle"
                        >
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(cat._id)}
                          className="text-gray-500 hover:text-red-500 hover:bg-red-500/10 p-2 rounded-lg transition-all duration-200"
                          data-testid={`category-delete-btn-${cat._id}`}
                          title="Kategoriyi sil"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {showAddModal && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4" data-testid="categories-add-modal">
            <div className="bg-slate-900 border border-slate-800 w-full max-w-md rounded-2xl p-8 shadow-2xl">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2"><Layers className="text-amber-500" /> Yeni Kategori</h3>
                <button onClick={() => setShowAddModal(false)} className="text-gray-400 hover:text-white text-2xl leading-none" data-testid="categories-add-modal-close">&times;</button>
              </div>
              <form onSubmit={handleAdd} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kategori Adı</label>
                  <input
                    required
                    placeholder="Örn: Spor"
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all"
                    value={newCat.name}
                    onChange={e => setNewCat({ ...newCat, name: e.target.value })}
                    data-testid="category-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">Tip</label>
                    <select
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none appearance-none cursor-pointer"
                      value={newCat.type}
                      onChange={e => setNewCat({ ...newCat, type: e.target.value })}
                      data-testid="category-type-select"
                    >
                      {TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">Sıra (Ops.)</label>
                    <input
                      type="number"
                      min="0"
                      placeholder="Otomatik"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none transition-all font-mono"
                      value={newCat.order || ''}
                      onChange={e => setNewCat({ ...newCat, order: parseInt(e.target.value) || 0 })}
                      data-testid="category-order-input"
                    />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 bg-slate-800 text-white font-bold py-4 rounded-xl hover:bg-slate-700 transition-colors"
                    data-testid="category-cancel-btn"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 bg-amber-500 text-black font-bold py-4 rounded-xl hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20"
                    data-testid="category-save-btn"
                  >
                    Kaydet
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ---- Edit Category Modal ---- */}
        {editCat && (
          <div className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-[100] p-4 overflow-y-auto" data-testid="categories-edit-modal">
            <div className="bg-slate-900 border border-amber-500/30 w-full max-w-md rounded-2xl p-7 shadow-2xl shadow-amber-500/10 max-h-[90vh] overflow-y-auto my-auto">
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Pencil className="text-amber-500" /> Kategoriyi Düzenle
                </h3>
                <button onClick={() => setEditCat(null)} type="button" className="text-gray-400 hover:text-white" data-testid="categories-edit-modal-close">
                  <X size={20} />
                </button>
              </div>
              <form onSubmit={handleEditSave} className="space-y-4">
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase ml-1">Kategori Adı</label>
                  <input
                    required
                    className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none"
                    value={editCat.name}
                    onChange={e => setEditCat({ ...editCat, name: e.target.value })}
                    data-testid="category-edit-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">Tip</label>
                    <select
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none cursor-pointer"
                      value={editCat.type}
                      onChange={e => setEditCat({ ...editCat, type: e.target.value })}
                      data-testid="category-edit-type"
                    >
                      {TYPE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase ml-1">Sıra</label>
                    <input
                      type="number"
                      min="0"
                      className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3.5 text-white focus:border-amber-500 outline-none font-mono"
                      value={editCat.order}
                      onChange={e => setEditCat({ ...editCat, order: parseInt(e.target.value) || 0 })}
                      data-testid="category-edit-order"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-800 cursor-pointer hover:border-amber-500/40 transition">
                  <input
                    type="checkbox"
                    checked={!!editCat.is_active}
                    onChange={e => setEditCat({ ...editCat, is_active: e.target.checked })}
                    className="w-5 h-5 accent-amber-500"
                    data-testid="category-edit-active"
                  />
                  <span className="text-sm text-white">Aktif</span>
                </label>

                {editErr && (
                  <div className="flex items-start gap-2 p-3 rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-xs">
                    <X size={14} className="shrink-0 mt-0.5" /> <span>{editErr}</span>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setEditCat(null)}
                    className="flex-1 bg-slate-800 text-white font-bold py-3 rounded-xl hover:bg-slate-700 transition-colors"
                    data-testid="category-edit-cancel"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    disabled={editBusy}
                    className="flex-1 bg-amber-500 text-black font-bold py-3 rounded-xl hover:bg-amber-600 disabled:opacity-60 transition-colors shadow-lg shadow-amber-500/20 flex items-center justify-center gap-2"
                    data-testid="category-edit-save"
                  >
                    {editBusy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                    {editBusy ? 'Kaydediliyor…' : 'Değişiklikleri Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
